import React, { useCallback } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import _ from "lodash";

import { actionCreators as noiCalculationDashboardActionCreators } from "../../reportingComponents/noiCalculationDashboard/noiRegularCalculationDashboard/noiRegularCalculationDashboard.store";
import { actionCreators as noiProjectedCalculationDashboardActionCreators } from "../../reportingComponents/noiCalculationDashboard/noiProjectedCalculationDashboard/noiProjectedCalculationDashboard.store";

import { StyledDoneButton } from "./styles";
import { updateLineItemsIfNoiFieldIsUsed } from "pages/lenderReporting/reportingComponents/noiCalculationDashboard/utils";

const calculateSinglePropertyComparison = (comparisonOperator, comparisonCards) => {
  const orderedComparisonCards = _.orderBy(comparisonCards, ["selectedField.value"], ["desc"]);

  return comparisonOperator === "greaterOf" ?
    orderedComparisonCards[0].selectedField.value :
    orderedComparisonCards[comparisonCards.length - 1].selectedField.value;
};

const calculateMultiPropLineItemValue = propFields => propFields.reduce((total, pf) => (pf.selectedField ? total + pf.selectedField?.value : total), 0);

export const calculateSumForComparisonGroup = group => group.reduce((result, propField) => {
  if (propField.selectedField) {
    const { value } = propField.selectedField;
    if (_.isNumber(value)) {
      return result + value;
    }

    return result;
  }

  return result;
}, 0);

export const determineGroupIndexWithSelectedProperty = (comparisonOperator, comparisonGroups, groupPropFieldIdx) => {
  const propFieldValuesByGroup = comparisonGroups.map(group => group[groupPropFieldIdx]?.selectedField?.value || null);

  if (propFieldValuesByGroup.every(pfv => pfv === null)) {
    return null;
  }

  if (comparisonOperator === "greaterOf") {
    return propFieldValuesByGroup.indexOf(Math.max(...propFieldValuesByGroup));
  }

  return propFieldValuesByGroup.indexOf(Math.min(...propFieldValuesByGroup));
};

export const calculateMultiPropLineItemComparisonValueAndSelections = (calculationType, comparisonOperator, comparisonGroups) => {
  let selectedGroupIndex = null;
  let totalValue = null;
  const selectedPropertiesIndices = Array.from(comparisonGroups, _cg => []);

  // cumulative
  if (calculationType === "cumulative") {
    const groupValues = comparisonGroups.map(group => calculateSumForComparisonGroup(group));
    if (comparisonOperator === "greaterOf") {
      selectedGroupIndex = groupValues.indexOf(Math.max(...groupValues));
    } else {
      selectedGroupIndex = groupValues.indexOf(Math.min(...groupValues));
    }
    totalValue = groupValues[selectedGroupIndex];

    return { selectedGroupIndex, totalValue, selectedPropertiesIndices };
  }

  // individual
  totalValue = comparisonGroups[0].reduce((total, _propField, index) => {
    const groupIndexToSelect = determineGroupIndexWithSelectedProperty(comparisonOperator, comparisonGroups, index);
    if (groupIndexToSelect === null) {
      return total;
    }
    selectedPropertiesIndices[groupIndexToSelect].push(index);

    return total + comparisonGroups[groupIndexToSelect][index]?.selectedField?.value;
  }, 0);

  return { selectedGroupIndex, totalValue, selectedPropertiesIndices };
};

const InsertNewLineButton = props => {
  const {
    isMultipleProperties,
    addLineItemType,
    newLineItemName,
    newLineItemOperator,
    newLineItemType,
    selectedField,
    multiplePropertiesValueFields,
    multiplePropertiesComparisonFields,
    multiPropComparisonSubLineItemNames,
    singlePropertyComparisonCards,
    lineItems,
    comparisonOperator,
    adjustedExpenseLineItems,
    adjustedIncomeLineItems,
    lineItemToEdit,
    comparisonCalculationType,
    setNoiCalculationsDashboardSelections,
    getProjectedNoiCalcByReportComponentId,
    fields
  } = props;

  const isDoneButtonActive = useCallback(() => {
    if (!newLineItemName) {
      return false;
    }
    // multiple properties
    if (isMultipleProperties) {
      if (newLineItemType === "value") {
        return multiplePropertiesValueFields.some(prop => Boolean(prop.selectedField));
      }
      // comparison
      const areSubgroupNamesValid = multiPropComparisonSubLineItemNames.every(name => Boolean(name));
      if (!areSubgroupNamesValid) {
        return false;
      }

      // check if each selected field for property has a corresponding value to compare against
      let atLeastOnePropertySelectedInAllGroups = false;
      const selectionsForEachProp = {};
      multiplePropertiesComparisonFields[0].forEach(prop => {
        selectionsForEachProp[prop.propertyId] = [];
      });
      multiplePropertiesComparisonFields.forEach(group => {
        group.forEach(prop => {
          selectionsForEachProp[prop.propertyId].push(Boolean(prop.selectedField));
        });
      });

      for (const propId in selectionsForEachProp) {
        if (selectionsForEachProp.hasOwnProperty(propId)) {
          const isSelected = selectionsForEachProp[propId][0];
          const isMatchingInAllGroup = selectionsForEachProp[propId].every(selection => selection === isSelected);
          if (isSelected && isMatchingInAllGroup) {
            atLeastOnePropertySelectedInAllGroups = true;
          }
          if (!isMatchingInAllGroup) {
            return false;
          }
        }
      }

      return atLeastOnePropertySelectedInAllGroups;
    }

    // single property
    if (newLineItemType === "value") {
      return Boolean(selectedField);
    }

    return singlePropertyComparisonCards.every(card => Boolean(card.selectedField) && Boolean(card.name));
  }, [
    newLineItemName,
    isMultipleProperties,
    newLineItemType,
    multiplePropertiesValueFields,
    multiPropComparisonSubLineItemNames,
    multiplePropertiesComparisonFields,
    singlePropertyComparisonCards,
    selectedField
  ]);

  const handleAddOrEdit = () => {
    const mode = lineItemToEdit ? "edit" : "add";

    // determine expense or income
    const lineItemType = addLineItemType || lineItemToEdit?.type;

    let sortOrder;
    // eslint-disable-next-line prefer-destructuring
    if (mode === "edit") {
      sortOrder = lineItemToEdit.sortOrder;
    } else {
      sortOrder = lineItemType === "income" ? adjustedIncomeLineItems?.length || 0 : adjustedExpenseLineItems?.length || 0;
    }

    let lineItemToAddOrEdit = {
      isMultipleProperties,
      sortOrder,
      name: newLineItemName,
      type: lineItemType,
      operator: newLineItemOperator,
      lineType: newLineItemType,
      id: `${lineItemType}${sortOrder}`
    };

    if (!isMultipleProperties) {
      if (newLineItemType === "value") {
        lineItemToAddOrEdit = {
          ...lineItemToAddOrEdit,
          amount: selectedField.value,
          field: selectedField,
          comparisonFields: null,
          comparisonSubFieldNames: null,
          comparisonOperator: null,
          isComparison: false
        };
      } else {
        lineItemToAddOrEdit = {
          ...lineItemToAddOrEdit,
          amount: calculateSinglePropertyComparison(comparisonOperator, singlePropertyComparisonCards),
          field: null,
          comparisonFields: singlePropertyComparisonCards.map(card => card.selectedField),
          comparisonSubFieldNames: singlePropertyComparisonCards.map(card => card.name),
          comparisonOperator,
          isComparison: true
        };
      }
    } else if (newLineItemType === "value") {
      lineItemToAddOrEdit = {
        ...lineItemToAddOrEdit,
        multiplePropertiesValueFields,
        field: null,
        comparisonFields: null,
        comparisonSubFieldNames: null,
        comparisonOperator: null,
        isComparison: false,
        amount: calculateMultiPropLineItemValue(multiplePropertiesValueFields)
      };
    } else {
      const { totalValue, selectedGroupIndex, selectedPropertiesIndices } = calculateMultiPropLineItemComparisonValueAndSelections(
        comparisonCalculationType,
        comparisonOperator,
        multiplePropertiesComparisonFields
      );
      lineItemToAddOrEdit = {
        ...lineItemToAddOrEdit,
        comparisonCalculationType,
        multiplePropertiesComparisonFields,
        comparisonOperator,
        field: null,
        isComparison: true,
        amount: totalValue,
        comparisonSubFieldNames: multiPropComparisonSubLineItemNames,
        selectedGroupIndex,
        selectedPropertiesIndices
      };
    }

    let newItemsList;
    if (mode === "edit") {
      const filteredLineItems = lineItems.filter(item => item.id !== lineItemToEdit.id);
      newItemsList = [...filteredLineItems, lineItemToAddOrEdit];
    } else {
      newItemsList = [...lineItems, lineItemToAddOrEdit];
    }

    const updatedLineItems = updateLineItemsIfNoiFieldIsUsed(newItemsList, fields);

    setNoiCalculationsDashboardSelections({
      initialDrawerSectionName: null,
      lineItems: updatedLineItems,
      isLenderReportingDrawerOpen: false,
      addLineItemType: null,
      lineItemToEdit: null
    });
    getProjectedNoiCalcByReportComponentId({
      initialDrawerSectionName: null,
      lineItems: updatedLineItems,
      isLenderReportingDrawerOpen: false,
      addLineItemType: null,
      lineItemToEdit: null
    });
  };

  return <StyledDoneButton label="Done" disabled={!isDoneButtonActive()} onClick={handleAddOrEdit} />;
};

const mapStateToProps = ({
  lenderReportingNoiCalculation: { addLineItemType, lineItems, adjustedIncomeLineItems, adjustedExpenseLineItems, lineItemToEdit },
  lenderReportFieldManagement: { fields },
  lenderReportNewLineItemManagement
}) => ({
  ...lenderReportNewLineItemManagement,
  addLineItemType,
  lineItems,
  lineItemToEdit,
  adjustedIncomeLineItems,
  adjustedExpenseLineItems,
  fields
});

const mapDispatchToProps = dispatch => bindActionCreators({ ...noiCalculationDashboardActionCreators, ...noiProjectedCalculationDashboardActionCreators  }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(InsertNewLineButton);
